/**
 * districtStateEngine.js
 *
 * Singleton live district state. Holds the most recent pastoral reality of the
 * authenticated district, derived from the operational snapshot data.
 *
 * Updated by operationalContext after each parallel snapshot load — not on every
 * message, keeping it as a stable ambient background layer.
 *
 * Exposes:
 *   updateDistrictState({ ministryState, insights }, appScope)
 *   getDistrictState()
 *   getMinistryPressure()   → "low" | "medium" | "high" | "critical" | null
 *   getPastoralLoad()       → 0.0 – 1.0
 *   getAttentionItems()     → PriorityItem[]
 *   clearDistrictState()
 */

import { buildPriorityReport } from "./priorityEngine";

let _state = null;

/**
 * Update the live district state from freshly synthesized snapshot data.
 * Call after synthesizeContext() in operationalContext to avoid recomputing.
 *
 * @param {{ ministryState: Object, insights: import("./pastoralReasoningEngine").PastoralInsight[] }} synthesized
 * @param {Object} appScope
 */
export function updateDistrictState({ ministryState, insights }, appScope) {
  if (!ministryState) return;
  const priorityReport = buildPriorityReport(ministryState, insights ?? []);
  _state = {
    ministryState,
    insights:     insights ?? [],
    priorityReport,
    scope:        appScope,
    lastUpdated:  Date.now(),
  };
}

/** Full district state object, or null if not yet loaded. */
export function getDistrictState() { return _state; }

/** "low" | "medium" | "high" | "critical" | null */
export function getMinistryPressure() { return _state?.priorityReport?.pressure ?? null; }

/** 0.0 – 1.0 */
export function getPastoralLoad() { return _state?.priorityReport?.pastoralLoad ?? 0; }

/** PriorityItem[] sorted by urgency */
export function getAttentionItems() { return _state?.priorityReport?.items ?? []; }

/** true if state is within ttlMs of its last update */
export function isStateFresh(ttlMs = 5 * 60 * 1000) {
  return !!_state && (Date.now() - _state.lastUpdated) < ttlMs;
}

/** Clear on scope change or logout */
export function clearDistrictState() { _state = null; }
